import { useNavigate, useSearchParams } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Button, IconButton, InputAdornment } from "@mui/material";

import LOGO from "../../semiauth.logo.png";
import TermsAndConditions from "../utils/TermsAndConditions";
import { useVaultInviteDetails, useVaultClaim } from "../../hooks/vault/base";

const Claim = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [agreed, setAgreed] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleAgreed = (e) => setAgreed(e.target.checked);
  const togglePassword = () => setShowPassword(!showPassword);
  const toggleConfirmation = () => setShowConfirmation(!showConfirmation);

  const claim = useVaultClaim();
  const details = useVaultInviteDetails(token);

  const disabled =
    !firstName || !lastName || !password || !passwordConfirmation || !agreed;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    claim.mutate({
      organizationId: details.data?.organization.id || "",
      email: details.data?.email || "",
      lastName,
      firstName,
      password,
      passwordConfirmation,
    });
  };

  useEffect(() => {
    if (claim.isSuccess) navigate(`/accept-invitation?token=${token}`);
  }, [claim.isSuccess]);

  return (
    <Container
      maxWidth="sm"
      component="main"
      sx={{
        display: "flex",
        alignItems: "center",
        marginBlock: "auto",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img src={LOGO} alt="[semiauth] logo" className="logo" />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="given-name"
                name="firstName"
                required
                fullWidth
                id="firstName"
                label="First name"
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Last name"
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                disabled
                fullWidth
                id="email"
                label="Email"
                name="email"
                value={details.data?.email}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="password"
                name="password"
                label="Password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="toggle code visibility"
                        onClick={togglePassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="confirmation"
                name="confirmation"
                label="Confirm password"
                autoComplete="password-confirmation"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                type={showConfirmation ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="toggle code visibility"
                        onClick={toggleConfirmation}
                        edge="end"
                      >
                        {showConfirmation ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} textAlign="left">
              <TermsAndConditions
                type="platform"
                agreed={agreed}
                handleAgreed={handleAgreed}
              />
            </Grid>
          </Grid>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={disabled}
            sx={{ my: 2 }}
          >
            Join
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Claim;
